import React, { useEffect, useState } from "react";
import { Plus, Activity, Info } from "lucide-react";
import api, { apiError } from "../../api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth";
import { Button, Card, Input, Field, Select, Textarea, Modal, Badge, Spinner, EmptyState } from "../../components/ui";
import { toast } from "sonner";

export default function HillDT() {
  const { t } = useI18n();
  const { user } = useAuth();
  const enabled = user.clinic?.hill_dt_enabled;
  const [sessions, setSessions] = useState(null);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/hilldt/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  useEffect(() => { if (enabled) { load(); api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}); } else setSessions([]); }, [enabled]);
  const pName = (id) => { const p = patients.find((x) => x.id === id); return p ? `${p.first_name} ${p.last_name}` : id?.slice(0, 8); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div><h1 className="font-head font-bold text-navy text-2xl flex items-center gap-2"><Activity className="text-teal" /> {t("hilldt")}</h1>
          <p className="text-slate-500 text-sm">Parcours SpinaTech–Hill DT</p></div>
        {enabled && <Button className="ms-auto" onClick={() => setOpen(true)} data-testid="new-hilldt-btn"><Plus size={17} /> Nouvelle séance</Button>}
      </div>

      <div className="bg-skyx border border-blue-100 text-navy text-sm rounded-lg px-4 py-3 flex items-start gap-2">
        <Info size={17} className="mt-0.5 shrink-0" /> {t("hilldt_no_reco")}
      </div>

      {!enabled ? (
        <Card className="p-10 text-center">
          <Activity size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">{t("hilldt_disabled")}</p>
          <p className="text-sm text-slate-400 mt-1">Un administrateur peut l'activer dans les paramètres du cabinet.</p>
        </Card>
      ) : !sessions ? <Spinner /> : sessions.length === 0 ? <EmptyState title={t("no_data")} /> : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <Card key={s.id} className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div><div className="font-medium text-navy">{pName(s.patient_id)}</div>
                  <div className="text-xs text-slate-400">{s.session_date} · {s.mode || "—"} · {s.duration || "—"}</div></div>
                <Badge tone="navy">{s.region}</Badge>
                <div className="ms-auto flex items-center gap-3 text-sm">
                  <span className="text-slate-500">Douleur {s.pain_before}→<b className="text-teal">{s.pain_after}</b></span>
                  {s.adverse_event && s.adverse_event !== "Aucun" && <Badge tone="alert">{s.adverse_event}</Badge>}
                  {s.finalized && <Badge tone="green">finalisée</Badge>}
                </div>
              </div>
              {s.observations && <p className="text-sm text-slate-500 mt-2">{s.observations}</p>}
            </Card>
          ))}
        </div>
      )}

      <HillModal open={open} onClose={() => setOpen(false)} onDone={load} patients={patients} t={t} />
    </div>
  );
}

function HillModal({ open, onClose, onDone, patients, t }) {
  const base = { patient_id: "", session_date: new Date().toISOString().slice(0, 10), region: "lombaire",
    mode: "", duration: "", force_initial: "", force_max: "", angle: "", traction_ratio: "",
    pain_before: 0, pain_after: 0, radiation_before: "", radiation_after: "", tolerance: "",
    observations: "", adverse_event: "Aucun", action_taken: "", finalized: false };
  const [f, setF] = useState(base);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { setF(base); }, [open]);

  const submit = async () => {
    if (!f.patient_id) { toast.error("Sélectionnez un patient"); return; }
    setBusy(true);
    try { await api.post("/hilldt/sessions", f); toast.success("Séance enregistrée"); onClose(); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="Séance Hill DT" size="lg">
      <div className="space-y-3">
        <p className="text-xs text-warn bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">Tous les paramètres sont saisis et approuvés par le professionnel. Aucune valeur n'est suggérée.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t("patients")}><Select value={f.patient_id} onChange={(e) => set("patient_id", e.target.value)} data-testid="hill-patient">
            <option value="">—</option>{patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}</Select></Field>
          <Field label="Date"><Input type="date" value={f.session_date} onChange={(e) => set("session_date", e.target.value)} /></Field>
          <Field label="Région"><Select value={f.region} onChange={(e) => set("region", e.target.value)}><option value="lombaire">Lombaire</option><option value="cervicale">Cervicale</option></Select></Field>
          <Field label="Mode (saisi)"><Input value={f.mode} onChange={(e) => set("mode", e.target.value)} /></Field>
          <Field label="Durée"><Input value={f.duration} onChange={(e) => set("duration", e.target.value)} /></Field>
          <Field label="Force initiale"><Input value={f.force_initial} onChange={(e) => set("force_initial", e.target.value)} /></Field>
          <Field label="Force maximale"><Input value={f.force_max} onChange={(e) => set("force_max", e.target.value)} /></Field>
          <Field label="Angle"><Input value={f.angle} onChange={(e) => set("angle", e.target.value)} /></Field>
          <Field label="Ratio traction/relaxation"><Input value={f.traction_ratio} onChange={(e) => set("traction_ratio", e.target.value)} /></Field>
          <Field label="Tolérance"><Input value={f.tolerance} onChange={(e) => set("tolerance", e.target.value)} /></Field>
          <Field label="Douleur avant (0-10)"><Input type="number" min="0" max="10" value={f.pain_before} onChange={(e) => set("pain_before", Number(e.target.value))} /></Field>
          <Field label="Douleur après (0-10)"><Input type="number" min="0" max="10" value={f.pain_after} onChange={(e) => set("pain_after", Number(e.target.value))} /></Field>
        </div>
        <Field label="Observations"><Textarea rows={2} value={f.observations} onChange={(e) => set("observations", e.target.value)} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Événement indésirable"><Input value={f.adverse_event} onChange={(e) => set("adverse_event", e.target.value)} /></Field>
          <Field label="Action prise"><Input value={f.action_taken} onChange={(e) => set("action_taken", e.target.value)} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.finalized} onChange={(e) => set("finalized", e.target.checked)} className="range-teal" /> Finaliser la séance</label>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>{t("cancel")}</Button><Button onClick={submit} disabled={busy} data-testid="hill-submit">{t("save")}</Button></div>
      </div>
    </Modal>
  );
}
